import type { Material } from "@babylonjs/core/Materials/material";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
// Side effect: Mesh.createInstance (the wheels are instances of one wheel mesh).
import "@babylonjs/core/Meshes/instancedMesh";
import type { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";
import { TRUCK_SPEC } from "@townsy/world";
import { ctx2d, makeDynamicTexture } from "../../scene/core/materials";
import { PartBatch, box, cylinder } from "../../scene/core/meshKit";
import { TRUCK_PAINTS } from "./palette";

/** Wheel centre positions (local, +z = front): x = +-WHEEL_X, z = +-wheelbase / 2, y = wheel radius. */
export const WHEEL_X = 1.13;
const R = TRUCK_SPEC.wheelRadius;
const HALF_WB = TRUCK_SPEC.wheelbase / 2;

export interface TruckModel {
  root: TransformNode;
  /** Pitches (nose up = negative rotation.x) around the axle midpoint. */
  body: TransformNode;
  wheels: (Mesh | InstancedMesh)[];
  meshes: (Mesh | InstancedMesh)[];
  dispose(): void;
}

/** One tyre with its tread blocks and rim, axis along x, centred at the origin. */
export function buildWheelMesh(scene: Scene, name: string): Mesh {
  const b = new PartBatch();
  const tyre = cylinder(scene, `${name}_t`, { height: 0.74, diameter: R * 2, tessellation: 18 }, "#1E1E20", { rot: { z: Math.PI / 2 } });
  b.add(tyre);
  // Chunky tread blocks round the tyre, alternating sides.
  const blocks = 18;
  for (let i = 0; i < blocks; i++) {
    const a = (i / blocks) * Math.PI * 2;
    const off = i % 2 === 0 ? 0.17 : -0.17;
    const m = box(scene, `${name}_b${i}`, 0.34, 0.14, 0.2, "#2A2A2C", { pos: { x: off, y: Math.cos(a) * (R + 0.03), z: Math.sin(a) * (R + 0.03) }, rot: { x: -a } });
    b.add(m);
  }
  for (const side of [-1, 1]) {
    b.add(cylinder(scene, `${name}_r${side}`, { height: 0.06, diameter: 0.95, tessellation: 14 }, "#B9BEC6", { pos: { x: side * 0.37 }, rot: { z: Math.PI / 2 } }));
    b.add(cylinder(scene, `${name}_h${side}`, { height: 0.1, diameter: 0.34, tessellation: 10 }, "#6C717A", { pos: { x: side * 0.4 }, rot: { z: Math.PI / 2 } }));
  }
  const mesh = b.build(name, scene)!;
  mesh.isPickable = false;
  return mesh;
}

/** Number board texture: the truck's number on a white roundel. */
export function numberTexture(scene: Scene, n: number, paint: { body: string; trim: string }): DynamicTexture {
  const tex = makeDynamicTexture(scene, `truckNum${n}`, 128, 128, { alpha: true, mips: true });
  const ctx = ctx2d(tex);
  ctx.clearRect(0, 0, 128, 128);
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(64, 64, 60, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = paint.trim;
  ctx.stroke();
  ctx.fillStyle = "#151515";
  ctx.font = "900 92px system-ui, 'Segoe UI', sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(n), 64, 70);
  tex.update();
  return tex;
}

/**
 * An original, chunky low-poly monster truck: a pickup-style body in the truck's paint high above four huge tyres, a
 * tube frame, shocks, a light bar and number roundels on both doors and the roof. Local origin = the ground under the
 * axle midpoint; +z = the nose.
 */
export function buildTruckModel(scene: Scene, id: string, number: number, paintIndex: number, wheelProto: Mesh, vertexMaterial: Material): TruckModel & { numberMaterial: StandardMaterial; numberTex: DynamicTexture } {
  const paint = TRUCK_PAINTS[paintIndex % TRUCK_PAINTS.length]!;
  const root = new TransformNode(`truck_${id}`, scene);
  const body = new TransformNode(`truck_${id}_body`, scene);
  body.parent = root;
  body.position.y = R;
  const b = new PartBatch();
  const base = R; // body-node origin sits at axle height
  const y = (worldY: number) => worldY - base;
  // Frame rails, axles, shocks.
  b.boxBase(0.22, 0.24, 5.2, "#2B2D33", -0.55, y(1.05), 0);
  b.boxBase(0.22, 0.24, 5.2, "#2B2D33", 0.55, y(1.05), 0);
  for (const z of [-HALF_WB, HALF_WB]) {
    b.box(2.0, 0.22, 0.26, "#34363D", 0, 0, z);
    b.box(0.5, 0.36, 0.5, "#4B4F57", 0, 0.05, z);
    for (const side of [-1, 1]) {
      // Shocks: angled from the axle to the frame (a long-travel look).
      b.box(0.16, 1.05, 0.16, paint.trim, side * 0.72, 0.5, z + (z > 0 ? -0.25 : 0.25), z > 0 ? 0.0 : 0.0);
    }
  }
  // Body: hood, cab, bed (in the paint), black flares over the wheels.
  b.boxBase(2.36, 0.62, 2.0, paint.body, 0, y(1.62), 1.75);
  b.boxBase(2.3, 0.08, 1.7, paint.trim, 0, y(2.24), 1.65); // hood stripe
  b.boxBase(2.26, 1.15, 1.6, paint.body, 0, y(1.62), 0.0);
  b.boxBase(2.1, 0.62, 1.48, paint.body, 0, y(2.72), -0.05); // cabin
  b.boxBase(2.12, 0.5, 1.2, "#1C2733", 0, y(2.78), 0.02); // glass band
  b.boxBase(2.24, 0.1, 1.56, paint.body, 0, y(3.28), -0.05); // roof
  b.boxBase(2.36, 0.55, 2.1, paint.body, 0, y(1.62), -1.9); // bed
  // Bed floor: its top 2 cm above the bed box's top (2.17 m). Level with it, the two faces fought for the same pixels and
  // the bed flickered between paint and black (founder report 2026-10-13).
  b.boxBase(2.0, 0.12, 1.9, "#2B2D33", 0, y(2.07), -1.9);
  for (const side of [-1, 1]) {
    for (const z of [-HALF_WB, HALF_WB]) {
      b.boxBase(0.5, 0.18, 1.9, "#151618", side * 1.16, y(1.6), z);
    }
  }
  // Bumpers, grille, lights, light bar and roll bar.
  b.boxBase(2.5, 0.3, 0.28, "#151618", 0, y(1.38), 2.86);
  b.boxBase(2.5, 0.3, 0.28, "#151618", 0, y(1.38), -2.86);
  b.boxBase(1.4, 0.36, 0.06, "#2E3138", 0, y(1.75), 2.76);
  for (const side of [-1, 1]) {
    b.boxBase(0.36, 0.2, 0.06, "#FFF6C8", side * 0.86, y(1.86), 2.77);
    b.boxBase(0.3, 0.16, 0.06, "#C81E1E", side * 0.95, y(1.8), -2.96);
  }
  b.boxBase(1.9, 0.12, 0.16, "#151618", 0, y(3.38), 0.55);
  for (let i = 0; i < 4; i++) b.boxBase(0.3, 0.14, 0.12, "#FFE36E", -0.66 + i * 0.44, y(3.4), 0.64);
  b.boxBase(0.1, 0.7, 0.1, "#151618", -0.9, y(2.17), -0.95);
  b.boxBase(0.1, 0.7, 0.1, "#151618", 0.9, y(2.17), -0.95);
  b.boxBase(1.9, 0.1, 0.1, "#151618", 0, y(2.82), -0.95);
  const mesh = b.build(`truck_${id}_mesh`, scene)!;
  mesh.material = vertexMaterial;
  mesh.parent = body;
  mesh.isPickable = false;
  mesh.receiveShadows = true;
  const meshes: (Mesh | InstancedMesh)[] = [mesh];

  // Number roundels: both doors and the roof.
  const numberTex = numberTexture(scene, number, paint);
  const numberMaterial = new StandardMaterial(`truck_${id}_numMat`, scene);
  numberMaterial.diffuseTexture = numberTex;
  numberMaterial.useAlphaFromDiffuseTexture = true;
  numberMaterial.specularColor = new Color3(0.05, 0.05, 0.05);
  numberMaterial.emissiveColor = new Color3(0.25, 0.25, 0.25);
  const plates: { pos: [number, number, number]; rot: [number, number, number] }[] = [
    { pos: [-1.145, y(2.18), 0.1], rot: [0, Math.PI / 2, 0] },
    { pos: [1.145, y(2.18), 0.1], rot: [0, -Math.PI / 2, 0] },
    { pos: [0, y(3.39), -0.1], rot: [Math.PI / 2, 0, 0] }, // 1 cm above the roof top (3.38 m)
  ];
  plates.forEach((p, i) => {
    const plane = CreatePlane(`truck_${id}_num${i}`, { size: 0.9 }, scene);
    plane.position.set(p.pos[0], p.pos[1], p.pos[2]);
    plane.rotation.set(p.rot[0], p.rot[1], p.rot[2]);
    plane.material = numberMaterial;
    plane.parent = body;
    plane.isPickable = false;
    meshes.push(plane);
  });

  const wheels: InstancedMesh[] = [];
  for (const z of [HALF_WB, -HALF_WB]) {
    for (const side of [-1, 1]) {
      const w = wheelProto.createInstance(`truck_${id}_w${wheels.length}`);
      w.parent = body;
      w.position.set(side * WHEEL_X, 0, z);
      w.isPickable = false;
      wheels.push(w);
      meshes.push(w);
    }
  }
  return {
    root,
    body,
    wheels,
    meshes,
    numberMaterial,
    numberTex,
    dispose() {
      for (const m of meshes) m.dispose();
      numberMaterial.dispose();
      numberTex.dispose();
      body.dispose();
      root.dispose();
    },
  };
}
